import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Inbox,
  Search,
  Download,
  Upload,
  FileDown,
  Trash2,
  Mail,
  MessageSquare,
  Send,
  Globe2,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const code = cc.toUpperCase();
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminLeads = () => {
  const [data, setData] = useState({ leads: [], total: 0, byCreator: [] });
  const [creatorFilter, setCreatorFilter] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { getAuthHeaders, credentials } = useAuth();

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (creatorFilter) params.set("username", creatorFilter);
      if (search) params.set("search", search);
      params.set("limit", "500");
      const res = await fetch(`/api/admin/leads?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error("No se pudieron cargar los leads");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error de red");
    } finally {
      setIsLoading(false);
    }
  }, [creatorFilter, search, getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(fetchLeads, 250);
    return () => clearTimeout(t);
  }, [fetchLeads]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (creatorFilter) params.set("username", creatorFilter);
    const url = `/api/admin/leads/export?${params.toString()}`;
    // Use Basic auth via Authorization header by fetching blob
    fetch(url, { headers: { ...getAuthHeaders() } })
      .then((r) => {
        if (!r.ok) throw new Error("Export failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `leads_${creatorFilter || "all"}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("CSV descargado");
      })
      .catch(() => toast.error("Error al exportar"));
    // unused but kept for ESLint about credentials
    void credentials;
  };

  const downloadTemplate = () => {
    fetch(`/api/admin/leads/template`, { headers: { ...getAuthHeaders() } })
      .then((r) => {
        if (!r.ok) throw new Error("Template fetch failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "plantilla_leads.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Plantilla descargada");
      })
      .catch(() => toast.error("No se pudo descargar la plantilla"));
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Debe ser un archivo .csv");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (creatorFilter) fd.append("defaultCreatorUsername", creatorFilter);
    toast.message("Importando…", { description: file.name });
    try {
      const r = await fetch(`/api/admin/leads/import`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: fd,
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible. Haz Deploy.");
        return;
      }
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al importar");
        return;
      }
      const summary =
        `✅ ${d.inserted} nuevos · ` +
        `⏭ ${d.skippedDuplicates} duplicados · ` +
        `⚠ ${d.skippedInvalid} inválidos`;
      toast.success(summary, {
        description: d.errors?.length ? d.errors.slice(0, 3).join("\n") : undefined,
        duration: 8000,
      });
      fetchLeads();
    } catch {
      toast.error("Error de red");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("¿Eliminar este lead?")) return;
    try {
      const res = await fetch(`/api/admin/leads/${id}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        toast.success("Lead eliminado");
        fetchLeads();
      } else {
        toast.error("No se pudo eliminar");
      }
    } catch {
      toast.error("Error de red");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Inbox className="w-7 h-7" />
              Leads capturados
            </h1>
            <p className="text-white/60">
              Personas que se registraron por contenido exclusivo
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={downloadTemplate}
              data-testid="download-leads-template-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg font-medium text-sm"
              title="Descargar plantilla CSV"
            >
              <FileDown className="w-4 h-4" />
              Plantilla
            </button>
            <label
              data-testid="import-leads-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg font-medium text-sm cursor-pointer"
              title="Importar leads desde CSV"
            >
              <Upload className="w-4 h-4" />
              Importar
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={importCsv}
                className="hidden"
              />
            </label>
            <button
              onClick={exportCsv}
              data-testid="export-leads-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-black rounded-lg font-medium hover:bg-white/90 transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Stats by creator */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/5 rounded-xl border border-white/10 p-4 md:col-span-1">
            <p className="text-white/50 text-sm">Total leads</p>
            <p className="text-3xl font-bold text-white" data-testid="leads-total">
              {data.total}
            </p>
          </div>
          <div className="bg-white/5 rounded-xl border border-white/10 p-4 md:col-span-2">
            <p className="text-white/50 text-sm mb-2">Top modelos por leads</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCreatorFilter("")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  !creatorFilter
                    ? "bg-white text-black"
                    : "bg-white/10 text-white/70 hover:text-white"
                }`}
                data-testid="filter-all-creators"
              >
                Todas
              </button>
              {data.byCreator.slice(0, 8).map((c) => (
                <button
                  key={c.username}
                  onClick={() => setCreatorFilter(c.username)}
                  data-testid={`filter-creator-${c.username}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    creatorFilter === c.username
                      ? "bg-white text-black"
                      : "bg-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  @{c.username}
                  <span className="ml-2 opacity-70">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white/5 rounded-xl border border-white/10 p-3 flex items-center gap-3">
          <Search className="w-4 h-4 text-white/40 ml-2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email, nombre, WhatsApp o Telegram..."
            data-testid="leads-search-input"
            className="flex-1 bg-transparent text-white placeholder-white/30 outline-none text-sm"
          />
          {creatorFilter && (
            <button
              onClick={() => setCreatorFilter("")}
              className="text-xs text-white/50 hover:text-white px-2"
            >
              Limpiar filtro
            </button>
          )}
        </div>

        {/* Leads table */}
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid="leads-table">
          {isLoading ? (
            <div className="p-12 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          ) : data.leads.length === 0 ? (
            <div className="p-12 text-center text-white/40">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aún no hay leads capturados</p>
              <p className="text-sm mt-1">
                Activa la captación de leads en el perfil de una modelo para empezar
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="px-4 py-3 text-white/50 font-medium">Modelo</th>
                    <th className="px-4 py-3 text-white/50 font-medium">Contacto</th>
                    <th className="px-4 py-3 text-white/50 font-medium hidden md:table-cell">
                      Origen
                    </th>
                    <th className="px-4 py-3 text-white/50 font-medium hidden md:table-cell">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-white/50 font-medium w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-b border-white/5 hover:bg-white/5"
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-4 py-3 text-white/80 whitespace-nowrap">
                        @{lead.creatorUsername}
                      </td>
                      <td className="px-4 py-3 space-y-1">
                        {lead.name && (
                          <div className="flex items-center gap-2 text-white">
                            <UserIcon className="w-3.5 h-3.5 text-white/40" />
                            {lead.name}
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-2 text-white/80 text-xs">
                            <Mail className="w-3.5 h-3.5 text-white/40" />
                            <a
                              href={`mailto:${lead.email}`}
                              className="hover:text-white underline-offset-2 hover:underline"
                            >
                              {lead.email}
                            </a>
                          </div>
                        )}
                        {lead.whatsapp && (
                          <div className="flex items-center gap-2 text-white/80 text-xs">
                            <MessageSquare className="w-3.5 h-3.5 text-green-400" />
                            <a
                              href={`https://wa.me/${lead.whatsapp.replace(/[^\d]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-white underline-offset-2 hover:underline"
                            >
                              {lead.whatsapp}
                            </a>
                          </div>
                        )}
                        {lead.telegram && (
                          <div className="flex items-center gap-2 text-white/80 text-xs">
                            <Send className="w-3.5 h-3.5 text-sky-400" />
                            {lead.telegram}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/60 hidden md:table-cell">
                        {lead.country ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span>{flagEmoji(lead.countryCode)}</span>
                            {lead.city ? `${lead.city}, ` : ""}
                            {lead.country}
                          </span>
                        ) : (
                          <span className="text-white/30 inline-flex items-center gap-1.5">
                            <Globe2 className="w-3.5 h-3.5" />
                            Desconocido
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/60 hidden md:table-cell whitespace-nowrap">
                        {formatDate(lead.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteLead(lead.id)}
                          data-testid={`delete-lead-${lead.id}`}
                          className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Eliminar lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
